import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LaravelImageUpload from '../components/LaravelImageUpload';
import ticketShowService from '../services/ticketShowService';
import '../assets/panels.css';
import { saveDraft, isQuotaError } from '../lib/offlineDb';
import { triggerSync } from '../lib/inspectionSyncEngine';

const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering
const genUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

const TicketNoteAdd = () => {
  const { id } = useParams(); // ticket ID
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  
  const [formData, setFormData] = useState({
    description: ''
  });

  // Load ticket data
  useEffect(() => {
    loadTicketData();
  }, [id]);

  const loadTicketData = async () => {
    try {
      setLoading(true);
      const data = await ticketShowService.getTicket(id);
      setTicket(data);
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.error('Failed to load ticket data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultipleFilesUpload = (filesData) => {
    console.log('Multiple files uploaded for note:', filesData);
    setUploadedDocuments(filesData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    const hasOfflineAttachments = uploadedDocuments.some((d) => d.offline);

    // Offline, or some attachments weren't uploaded → queue for background sync.
    if (!isOnline || hasOfflineAttachments) {
      const queued = await queueNoteOffline();
      if (!queued) setSaving(false);
      return;
    }

    try {
      const noteData = {
        ...formData,
        ticket_id: id,
        // Add uploaded documents (S3 keys)
        attachments: uploadedDocuments.length > 0 ? uploadedDocuments.map(doc => ({
          name: doc.name,
          s3Key: doc.s3Key,
          size: doc.size,
          type: doc.type
        })) : undefined
      };

      console.log('📝 Submitting note with attachments:', {
        noteData: Object.keys(noteData),
        attachments: noteData.attachments?.length || 0
      });

      // Call API to create note
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ticketsnotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(noteData)
      });

      if (!response.ok) {
        throw new Error('Failed to create note');
      }

      const result = await response.json();
      console.log('Note created successfully:', result);

      toast.success('Note added successfully');
      
      // Redirect back to ticket details
      navigate(`/tickets/${id}`);
      
    } catch (error) {
      console.error('Error creating note:', error);
      // Network failure → fall back to the offline queue instead of losing it.
      const queued = await queueNoteOffline();
      if (queued) return;
      toast.error('Failed to create note');
      setSaving(false);
    }
  };

  // Queue the note (description + any attachments) for background sync.
  // Returns true if queued.
  const queueNoteOffline = async () => {
    // Offline attachments (raw blobs) must respect the offline size cap.
    const tooBig = uploadedDocuments.find(
      (d) => d.blob && !d.s3Key && d.size > OFFLINE_MAX_SIZE
    );
    if (tooBig) {
      toast.error(
        `While offline, images must be under ${OFFLINE_MAX_SIZE / (1024 * 1024)}MB. Add larger images when you’re back online.`
      );
      return false;
    }

    try {
      const photos = {};
      uploadedDocuments.forEach((doc) => {
        const id = genUuid();
        photos[id] = doc.s3Key
          ? {
              s3Key: doc.s3Key, // already on S3 — sync skips re-upload
              fileName: doc.name,
              mimeType: doc.type,
              size: doc.size,
              folder: 'documents',
            }
          : {
              blob: doc.blob,
              fileName: doc.name,
              mimeType: doc.type,
              size: doc.size,
              folder: 'documents',
            };
      });

      await saveDraft({
        id: genUuid(),
        ticketId: id,
        reportType: 'ticketNote',
        description: formData.description,
        photos,
        clientUuid: genUuid(),
        status: 'queued',
        attempts: 0,
        permanent: false,
        lastError: null,
      });
    } catch (err) {
      toast.error(
        isQuotaError(err)
          ? 'This device is out of storage, so the note could not be saved for offline upload.'
          : 'Offline storage is unavailable, so the note cannot be saved while offline.'
      );
      return false;
    }

    toast.info('Saved. Your note will upload automatically when you’re back online.');
    triggerSync();
    navigate(`/tickets/${id}`);
    return true;
  };

  const handleCancel = () => {
    navigate(`/tickets/${id}`);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default">
              <div className="panel-body text-center">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p>Loading ticket data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default">
              <div className="panel-body text-center">
                <p>Ticket not found</p>
                <button 
                  className="btn btn-default"
                  onClick={() => navigate('/tickets')}
                >
                  Back to Tickets
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const responsiveStyles = `
    .ticket-note-add-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 32px 120px;
      background: white;
    }

    .ticket-note-add-form-container {
      width: 100%;
    }

    .ticket-note-add-container .panel {
      border: none;
    }

    .ticket-note-add-container .panel-title {
      border: none;
    }

    @media (max-width: 1024px) {
      .ticket-note-add-container {
        padding: 24px 40px;
      }
    }

    @media (max-width: 768px) {
      .ticket-note-add-container {
        padding: 16px 20px;
      }
    }

    @media (max-width: 576px) {
      .ticket-note-add-container {
        padding: 12px 10px;
      }

      .ticket-note-add-container .breadcrumb-row {
        flex-wrap: wrap;
        gap: 4px;
      }

      .ticket-note-add-container .form-group textarea {
        font-size: 16px !important;
      }

      .ticket-note-add-container .panel-body {
        padding: 15px !important;
      }

      .ticket-note-add-container .btn {
        width: 100%;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div className="container-fluid ticket-note-add-container">
        {/* Breadcrumb */}
        <div className="breadcrumb-row">
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/tickets')}
          >
            <span className="breadcrumb-sep">/</span>
            Tickets
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate(`/tickets/${id}`)}
          >
            <span className="breadcrumb-sep">/</span>
            Ticket {id}
          </span>
          <div
            style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
          >
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-properties">Add Note</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="ticket-note-add-form-container">
          <div className="panel mb-4">
            <div className="panel-title">
              <h3>Create Ticket Note</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit}>
                {/* Description Field */}
                <div className="form-group" style={{ marginBottom: '20px', width: '100%' }}>
                  <label htmlFor="description" style={{
                    fontWeight: 'bold',
                    marginBottom: '5px',
                    display: 'block',
                    fontSize: '14px',
                    color: '#333'
                  }}>
                    Description:
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="8"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      resize: 'vertical'
                    }}
                    placeholder="Enter description..."
                  />
                </div>

                {/* Laravel Image Upload Section */}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <LaravelImageUpload
                    onFilesUpload={handleMultipleFilesUpload}
                    folder="documents"
                  />
                </div>

                {/* Form Actions */}
                <div style={{
                  marginTop: '20px',
                  textAlign: 'right',
                  paddingTop: '15px',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                    style={{
                      backgroundColor: '#1B2046',
                      borderColor: '#1B2046',
                      padding: '6px 12px',
                      fontSize: '14px',
                      fontWeight: 'normal'
                    }}
                  >
                    <i className="glyphicon glyphicon-plus" style={{ marginRight: '5px' }}></i>
                    {saving ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketNoteAdd;
